import groupBy from "lodash/groupBy";
import { Inline } from "PFComponents/containers";
import { Divider } from "PFComponents/divider";
import { Tile } from "PFComponents/tile";
import { useSkillsType } from "PFCore/hooks";
import { CustomType, Importance, Profile, Subtemplate, Template } from "PFCore/types";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { UseConflictResolverReturn } from "../hooks/use_conflict_resolver";
import { AttributeSubsection } from "./attribute_subsection";
import css from "./attributes_box.module.scss";
import { AttributesEmptyState } from "./attributes_empty_state";
import { FrameworkValueTooltip } from "./framework_value_tooltip";

type AttributesBoxProps = UseConflictResolverReturn["resolvedAttributes"] & {
  template: Template;
  subtemplate: Subtemplate | null;
  activityCreator: Profile | null;
};

const VerticalDivider = () => <Divider type="vertical" color="paletteNeutral1" className={css.divider} />;

export const AttributesBox = ({
  skills,
  customFields,
  template,
  subtemplate,
  activityCreator
}: AttributesBoxProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "form.sections.skillsFrameworks.sidePanel" });
  const skillsType = useSkillsType();

  const skillsRenderData = useMemo(() => {
    const skillsByImportance = groupBy(skills, "importance");

    return [
      {
        id: Importance.Essential,
        title: t("essentialSkills"),
        items: skillsByImportance[Importance.Essential] || []
      },
      {
        id: Importance.Supporting,
        title: t("supportingSkills"),
        items: skillsByImportance[Importance.Supporting] || []
      }
    ].filter(({ items }) => items.length > 0);
  }, [skills, t]);

  const commonProps = {
    template,
    subtemplate,
    activityCreator
  };

  return (
    <Tile paddingVariant="none" border>
      {skills.length === 0 && customFields.length === 0 && <AttributesEmptyState />}
      {skillsRenderData.map(({ id, title, items }) => (
        <AttributeSubsection key={id} title={title} customType={skillsType as CustomType} {...commonProps}>
          {items.map((skill, index) => (
            <Inline key={skill.skillId} alignItems="center">
              <FrameworkValueTooltip
                name={String(skill.value)}
                requiredExperience={skill.requiredExperience}
                frameworks={skill.frameworks}
                tagIds={skill.tagIds}
              />
              {index < items.length - 1 && <VerticalDivider />}
            </Inline>
          ))}
        </AttributeSubsection>
      ))}
      {customFields.map(({ type, values }) => (
        <AttributeSubsection
          key={type.id}
          title={type.displayAs || ""}
          customType={type as CustomType}
          {...commonProps}
        >
          {values.map((customValue, index) => (
            <Inline key={customValue.customValueId}>
              <FrameworkValueTooltip name={String(customValue.value)} frameworks={customValue.frameworks} />
              {index < values.length - 1 && <VerticalDivider />}
            </Inline>
          ))}
        </AttributeSubsection>
      ))}
    </Tile>
  );
};
